import { useState } from "react";
import { Check, Globe, Search } from "lucide-react";
import { useTranslation } from "react-i18next";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

const regions = [
  { code: "US", nameKey: "regions.us" },
  { code: "GB", nameKey: "regions.gb" },
  { code: "CA", nameKey: "regions.ca" },
  { code: "AU", nameKey: "regions.au" },
  { code: "DE", nameKey: "regions.de" },
  { code: "FR", nameKey: "regions.fr" },
  { code: "JP", nameKey: "regions.jp" },
  { code: "IN", nameKey: "regions.in" },
  { code: "BR", nameKey: "regions.br" },
  { code: "MX", nameKey: "regions.mx" },
  { code: "KR", nameKey: "regions.kr" },
  { code: "ES", nameKey: "regions.es" },
  { code: "IE", nameKey: "regions.ie" },
  { code: "NZ", nameKey: "regions.nz" },
  { code: "IT", nameKey: "regions.it" },
  { code: "NL", nameKey: "regions.nl" },
  { code: "SE", nameKey: "regions.se" },
  { code: "NO", nameKey: "regions.no" },
  { code: "DK", nameKey: "regions.dk" },
  { code: "FI", nameKey: "regions.fi" },
];

interface RegionPickerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  currentRegion: string;
  onSelect: (code: string) => void;
}

const RegionPicker = ({ open, onOpenChange, currentRegion, onSelect }: RegionPickerProps) => {
  const { t } = useTranslation();
  const [search, setSearch] = useState("");
  const filtered = regions.filter((r) =>
    t(r.nameKey).toLowerCase().includes(search.toLowerCase())
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-sm mx-auto rounded-2xl max-h-[80vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Globe className="w-5 h-5 text-primary" />
            {t("regionPicker.title")}
          </DialogTitle>
        </DialogHeader>
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <input
            type="text"
            placeholder={t("regionPicker.searchPlaceholder")}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-10 pr-4 py-2.5 rounded-xl bg-secondary border border-border text-foreground text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/50"
          />
        </div>
        <div className="flex-1 overflow-y-auto space-y-1 max-h-[50vh]">
          {filtered.map((region) => (
            <button
              key={region.code}
              onClick={() => {
                onSelect(region.code);
                onOpenChange(false);
              }}
              className={`w-full flex items-center justify-between px-4 py-2.5 rounded-xl transition-all ${
                currentRegion === region.code
                  ? "bg-primary/15 border border-primary"
                  : "bg-secondary/50 border border-transparent hover:border-border"
              }`}
            >
              <span className="text-sm font-medium text-foreground">{t(region.nameKey)}</span>
              {currentRegion === region.code && <Check className="w-4 h-4 text-primary" />}
            </button>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default RegionPicker;
